import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Award, GraduationCap } from "lucide-react";
import { portfolioAPI } from "../../services/api";
import { AboutSkeleton } from "../../components/Skeleton/PageSkeletons";

const AboutContainer = styled.div`
  padding: 80px 24px 24px;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 400;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 8px;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.surface};

  h2 {
    font-size: 16px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};
    margin: 0;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.theme.colors.textTertiary};
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const InfoContent = styled.div`
  flex: 1;

  .label {
    font-size: 12px;
    color: ${props => props.theme.colors.textTertiary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  .value {
    font-size: 14px;
    color: ${props => props.theme.colors.textPrimary};
    line-height: 1.4;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const SkillCategory = styled.div`
  h3 {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};
    margin-bottom: 12px;
    padding-bottom: 4px;
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkillBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: ${props => props.theme.colors.surfaceSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
  transition: all 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.hover};
    border-color: ${props => props.theme.colors.primary};
  }

  .skill-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const ExperienceTimeline = styled.div`
  position: relative;
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  padding-left: 24px;
  padding-bottom: 24px;

  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 7px;
    top: 24px;
    bottom: -8px;
    width: 2px;
    background-color: ${props => props.theme.colors.border};
  }

  &::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 4px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const ExperienceContent = styled.div`
  .company {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};
    margin-bottom: 2px;
  }

  .role {
    font-size: 13px;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 4px;
  }

  .duration {
    font-size: 12px;
    color: ${props => props.theme.colors.textTertiary};
    margin-bottom: 8px;
  }

  .description {
    font-size: 13px;
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.4;
  }
`;

const CertificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CertificationItem = styled.div`
  padding: 16px;
  background-color: ${props => props.theme.colors.surfaceSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};

  .cert-name {
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme.colors.textPrimary};
    margin-bottom: 4px;
  }

  .cert-issuer {
    font-size: 12px;
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: 2px;
  }

  .cert-date {
    font-size: 12px;
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const About = () => {
  const [personalData, setPersonalData] = useState(null);
  const [skills, setSkills] = useState(null);
  const [experience, setExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          personalResponse,
          skillsResponse,
          experienceResponse,
          educationResponse,
          certificationsResponse,
        ] = await Promise.all([
          portfolioAPI.getPersonal(),
          portfolioAPI.getSkills(),
          portfolioAPI.getExperience(),
          portfolioAPI.getEducation(),
          portfolioAPI.getCertifications(),
        ]);

        setPersonalData(personalResponse.data.data);
        setSkills(skillsResponse.data.data);
        setExperience(experienceResponse.data.data);
        setEducation(educationResponse.data.data);
        setCertifications(certificationsResponse.data.data);
      } catch (error) {
        console.error("Error fetching about data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Technology logos mapping (using reliable CDNs)
  const techLogos = {
    "Node.js":
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/nodejs/nodejs-original.svg",
    "Express.js":
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/express/express-original.svg",
    NestJS:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/nestjs/nestjs-plain.svg",
    Python:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/python/python-original.svg",
    "React.js":
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/react/react-original.svg",
    JavaScript:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/javascript/javascript-original.svg",
    TypeScript:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/typescript/typescript-original.svg",
    HTML5:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/html5/html5-original.svg",
    CSS3: "https://raw.githubusercontent.com/devicons/devicon/master/icons/css3/css3-original.svg",
    MongoDB:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/mongodb/mongodb-original.svg",
    PostgreSQL:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/postgresql/postgresql-original.svg",
    DynamoDB:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/amazonwebservices/amazonwebservices-original.svg",
    Docker:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/docker/docker-original.svg",
    AWS: "https://raw.githubusercontent.com/devicons/devicon/master/icons/amazonwebservices/amazonwebservices-original.svg",
    GCP: "https://raw.githubusercontent.com/devicons/devicon/master/icons/googlecloud/googlecloud-original.svg",
    Git: "https://raw.githubusercontent.com/devicons/devicon/master/icons/git/git-original.svg",
    GitHub:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/github/github-original.svg",
    Firebase:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/firebase/firebase-plain.svg",
    Redis:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/redis/redis-original.svg",
    "Socket.io":
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/socketio/socketio-original.svg",
    Stripe:
      "https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/stripe.svg",
    Pinecone: "https://avatars.githubusercontent.com/u/44732606?s=200&v=4",
    Supabase:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/supabase/supabase-original.svg",
    Qdrant: "https://avatars.githubusercontent.com/u/73204753?s=200&v=4",
    OpenAI:
      "https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/openai.svg",
    JWT: "https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/jsonwebtokens.svg",
    Postman:
      "https://raw.githubusercontent.com/devicons/devicon/master/icons/postman/postman-original.svg",
    Agora: "https://avatars.githubusercontent.com/u/18050969?s=200&v=4",
  };

  // Function to get logo for skills that might have complex names
  const getSkillLogo = skill => {
    // Direct match first
    if (techLogos[skill]) {
      return techLogos[skill];
    }

    // Handle AWS variations and services
    if (
      skill.includes("AWS") ||
      skill.includes("Lambda") ||
      skill.includes("S3") ||
      skill.includes("DynamoDB") ||
      skill.includes("EC2") ||
      skill.includes("API Gateway") ||
      skill.includes("SES") ||
      skill.includes("SNS") ||
      skill.includes("SQS") ||
      skill.includes("CDK")
    ) {
      return techLogos["AWS"];
    }

    // Handle specific technologies within skill names
    const skillLower = skill.toLowerCase();
    if (skillLower.includes("node")) return techLogos["Node.js"];
    if (skillLower.includes("express")) return techLogos["Express.js"];
    if (skillLower.includes("nestjs") || skillLower.includes("nest"))
      return techLogos["NestJS"];
    if (skillLower.includes("python")) return techLogos["Python"];
    if (skillLower.includes("react")) return techLogos["React.js"];
    if (skillLower.includes("javascript") || skillLower.includes("js"))
      return techLogos["JavaScript"];
    if (skillLower.includes("typescript") || skillLower.includes("ts"))
      return techLogos["TypeScript"];
    if (skillLower.includes("html")) return techLogos["HTML5"];
    if (skillLower.includes("css")) return techLogos["CSS3"];
    if (skillLower.includes("mongo")) return techLogos["MongoDB"];
    if (skillLower.includes("postgres") || skillLower.includes("postgresql"))
      return techLogos["PostgreSQL"];
    if (skillLower.includes("dynamo")) return techLogos["DynamoDB"];
    if (skillLower.includes("docker")) return techLogos["Docker"];
    if (skillLower.includes("github") && skillLower.includes("hub"))
      return techLogos["Git/GitHub"];
    if (skillLower.includes("firebase")) return techLogos["Firebase"];
    if (skillLower.includes("redis")) return techLogos["Redis"];
    if (skillLower.includes("socket")) return techLogos["Socket.io"];
    if (skillLower.includes("stripe")) return techLogos["Stripe"];
    if (skillLower.includes("pinecone")) return techLogos["Pinecone"];
    if (skillLower.includes("supabase")) return techLogos["Supabase"];
    if (skillLower.includes("qdrant")) return techLogos["Qdrant"];
    if (skillLower.includes("openai")) return techLogos["OpenAI"];
    if (skillLower.includes("jwt")) return techLogos["JWT"];
    if (skillLower.includes("postman")) return techLogos["Postman"];
    if (skillLower.includes("agora")) return techLogos["Agora"];
    if (skillLower.includes("gcp") || skillLower.includes("google cloud"))
      return techLogos["GCP"];

    return null;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  if (loading) {
    return <AboutSkeleton />;
  }

  return (
    <AboutContainer>
      <Content>
        <PageHeader>
          <PageTitle>About Me</PageTitle>
          <PageDescription>
            Software Engineer specializing in cloud-native development and
            scalable solutions
          </PageDescription>
        </PageHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid>
            {/* Personal Information */}
            <Card variants={itemVariants}>
              <CardHeader>
                <h2>Personal Information</h2>
              </CardHeader>
              <CardContent>
                <InfoGrid>
                  <InfoItem>
                    <MapPin />
                    <InfoContent>
                      <div className="label">Location</div>
                      <div className="value">
                        {personalData?.location || "Karachi, Pakistan"}
                      </div>
                    </InfoContent>
                  </InfoItem>
                  <InfoItem>
                    <Mail />
                    <InfoContent>
                      <div className="label">Email</div>
                      <div className="value">
                        {personalData?.email || "hassan.m.8537@gmail.com"}
                      </div>
                    </InfoContent>
                  </InfoItem>
                  <InfoItem>
                    <Phone />
                    <InfoContent>
                      <div className="label">Phone</div>
                      <div className="value">
                        {personalData?.phone || "+923112213827"}
                      </div>
                    </InfoContent>
                  </InfoItem>
                </InfoGrid>
              </CardContent>
            </Card>

            {/* About Description */}
            <Card variants={itemVariants}>
              <CardHeader>
                <h2>Professional Summary</h2>
              </CardHeader>
              <CardContent>
                <p
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "var(--color-text-secondary)",
                    margin: 0,
                  }}
                >
                  {personalData?.bio ||
                    "Software Engineer with expertise in Node.js, Python, and cloud-native development. Proven track record building scalable real-time applications, RESTful APIs, and serverless solutions using AWS/GCP. Experienced in SQL/NoSQL databases, AI/ML integration, LLM/RAG systems, and modern DevOps practices."}
                </p>
              </CardContent>
            </Card>

            {/* Skills */}
            <Card variants={itemVariants} style={{ gridColumn: "1 / -1" }}>
              <CardHeader>
                <h2>Technical Skills</h2>
              </CardHeader>
              <CardContent>
                <SkillsGrid>
                  {skills &&
                    Object.entries(skills).map(([category, skillList]) => (
                      <SkillCategory key={category}>
                        <h3>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </h3>
                        <SkillsList>
                          {skillList.map((skill, index) => {
                            const skillLogo = getSkillLogo(skill);
                            return (
                              <SkillBadge key={index}>
                                {skillLogo && (
                                  <img
                                    src={skillLogo}
                                    alt={skill}
                                    className="skill-icon"
                                    onError={e =>
                                      (e.target.style.display = "none")
                                    }
                                  />
                                )}
                                {skill}
                              </SkillBadge>
                            );
                          })}
                        </SkillsList>
                      </SkillCategory>
                    ))}
                </SkillsGrid>
              </CardContent>
            </Card>

            {/* Experience */}
            <Card variants={itemVariants}>
              <CardHeader>
                <h2>Experience</h2>
              </CardHeader>
              <CardContent>
                <ExperienceTimeline>
                  {experience.map((exp, index) => (
                    <TimelineItem
                      key={exp.id}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: index * 0.1 }}
                    >
                      <ExperienceContent>
                        <div className="company">{exp.company}</div>
                        <div className="role">{exp.title}</div>
                        <div className="duration">
                          {exp.duration} • {exp.location}
                        </div>
                        <div className="description">{exp.description}</div>
                      </ExperienceContent>
                    </TimelineItem>
                  ))}
                </ExperienceTimeline>
              </CardContent>
            </Card>

            {/* Education & Certifications */}
            <Card variants={itemVariants}>
              <CardHeader>
                <h2>Education & Certifications</h2>
              </CardHeader>
              <CardContent>
                {/* Education */}
                <div style={{ marginBottom: "24px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "12px",
                      color: "var(--color-text-primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <GraduationCap size={16} />
                    Education
                  </h3>
                  {education.map((edu, index) => (
                    <div key={edu.id} style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "var(--color-text-primary)",
                          marginBottom: "2px",
                        }}
                      >
                        {edu.degree}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--color-text-secondary)",
                          marginBottom: "2px",
                        }}
                      >
                        {edu.institution} • {edu.year}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--color-text-tertiary)",
                        }}
                      >
                        {edu.description}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Certifications */}
                <div>
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "12px",
                      color: "var(--color-text-primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Award size={16} />
                    Certifications
                  </h3>
                  <CertificationsList>
                    {certifications.map((cert, index) => (
                      <CertificationItem key={cert.id}>
                        <div className="cert-name">{cert.name}</div>
                        <div className="cert-issuer">{cert.issuer}</div>
                        <div className="cert-date">{cert.year}</div>
                      </CertificationItem>
                    ))}
                  </CertificationsList>
                </div>
              </CardContent>
            </Card>
          </Grid>
        </motion.div>
      </Content>
    </AboutContainer>
  );
};

export default About;
